import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic } from "lucide-react";
import { motion } from "framer-motion";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../lib/track";

export default function AuthCallback() {
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const ran = useRef(false);
    const [status, setStatus] = useState("Signing you in…");

    useEffect(() => {
        // Synchronous guard against StrictMode double-execution.
        if (ran.current) return;
        ran.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;

        if (!sessionId) {
            setStatus("No sign-in token found. Returning to sign in…");
            setTimeout(() => navigate("/auth", { replace: true }), 1200);
            return;
        }

        (async () => {
            try {
                const { data } = await api.post("/auth/google/session", { session_id: sessionId });
                setSession(data.token, data.user);
                trackEvent(data.user.onboarded ? "signin_completed" : "signup_completed", { provider: "google" });
                // Clear the URL fragment so a refresh doesn't re-trigger.
                window.history.replaceState(null, "", window.location.pathname);
                navigate(data.user.onboarded ? "/home" : "/onboarding", { replace: true });
            } catch (e) {
                const msg = e?.response?.data?.detail || "Couldn't complete Google sign-in";
                setStatus(msg + " — returning…");
                setTimeout(() => navigate("/auth", { replace: true }), 2000);
            }
        })();
    }, [navigate, setSession]);

    return (
        <div className="min-h-screen vf-grain flex items-center justify-center p-6" data-testid="auth-callback">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
            >
                <div className="inline-flex w-12 h-12 rounded-2xl bg-terracotta items-center justify-center text-white mb-5">
                    <Mic size={20} strokeWidth={1.5} />
                </div>
                <div className="font-serif text-3xl tracking-tight font-light italic text-stone-700">{status}</div>
                <div className="mt-3 flex items-center justify-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" style={{ animationDelay: "0.3s" }} />
                </div>
            </motion.div>
        </div>
    );
}
